import {
  ExteriorNodeLabelModel,
  ExteriorNodeLabelModelPosition,
  FreeEdgeLabelModel,
  FreeNodeLabelModel,
  IColumn,
  IEdge,
  IGraph,
  ILabel,
  ILabelModelParameter,
  IModelItem,
  INode,
  INodeStyle,
  IRow,
  Insets,
  NodeStyleStripeStyleAdapter,
  Point,
  Rect,
  Table,
  TableNodeStyle,
} from '@ardoq/yfiles';
import { isEqual } from 'lodash';
import * as encodingUtils from '@ardoq/html';
import { getDefaultSize, getStyle } from 'yfilesExtensions/styles/styleLoader';
import { ArdoqLabelStyle } from 'yfilesExtensions/styles/ArdoqLabelStyle';
import { ArdoqURLLabelStyle } from 'yfilesExtensions/styles/ardoqURLLabelStyle';
import { ArdoqEdgeStyle } from 'yfilesExtensions/styles/ArdoqEdgeStyle';
import { GraphEdge, GraphGroup, GraphItem, GraphNode } from '@ardoq/graph';
import { logError } from '@ardoq/logging';
import { ArdoqAddToScenario } from './styles/ArdoqAddToScenario';
import type { ArdoqId } from '@ardoq/api-types';
import { DiffType } from '@ardoq/data-model';
import { isInScopeDiffMode } from 'scope/scopeDiff';
import { getNodeLayoutSize } from './getNodeLayoutSize';
import { isContextNode } from './styles/nodeDecorator';
import {
  CONTEXT_HIGHLIGHT_PADDING,
  CONTEXT_HIGHLIGHT_STROKE,
  NODE_HEIGHT,
  NODE_WIDTH,
} from './styles/consts';
import { urlNodeButtonModelParameter } from 'tabview/blockDiagram/view/yFilesExtensions/labelParameters';
import { logWarn } from '@ardoq/sentry';
import type { Node, Edge } from 'graph/types';
import { ComponentBackboneModel } from 'aqTypes';
import { GraphItemModel } from 'graph/GraphItem';
import { GraphBuilderHasLayoutArgs } from './types';
import { ArdoqSwimlaneStripeStyle } from './styles/ArdoqSwimlaneStripeStyle';
import { ArdoqSwimlaneLabelStyle } from './styles/ArdoqSwimlaneLabelStyle';
import { ArdoqTableBackgroundStyle } from './styles/ArdoqTableBackgroundStyle';
import { FieldValue } from 'graph/FieldValue';

const PARENT_ROW_INSETS = new Insets(50, 50, 50, 175);
const CHILD_ROW_INSETS = new Insets(50, 50, 50, 150);

const PARENT_COLUMN_INSETS = new Insets(175, 50, 50, 50);
const CHILD_COLUMN_INSETS = new Insets(150, 50, 50, 50);

interface TableGraphBuilderHasLayoutArgs extends GraphBuilderHasLayoutArgs {
  rows: Node[];
}

type ConstructorArgs = {
  graph: IGraph;
  rowHeight?: number;
  columnWidth?: number;
};

type DiffUpdateGraphArgs<
  TItem extends IModelItem,
  TObj extends { id: string },
> = {
  oldMap: Map<string, TItem>;
  businessObjects: TObj[];
  createFunc: (graphItem: TObj) => TItem;
  updateFunc: (graphItem: TObj) => TItem;
  removeFunc?: (graphItem: TItem, map: Map<string, TItem>) => void;
};

const isURLLabel = (label: ILabel) => {
  return label.style instanceof ArdoqURLLabelStyle;
};

export class TableGraphBuilder {
  groupsSource: (Node | GraphGroup)[];
  nodesSource: (Node | GraphNode)[];
  scenarioRelatedComponentIds: ReadonlySet<ArdoqId> = new Set();
  edgesSource: (Edge | GraphEdge)[];
  private _groupNodeMap: Map<string, INode>;
  protected _nodeMap: Map<string, INode>;
  protected _edgeMap: Map<string, IEdge>;
  private hasGroups: boolean;
  private urlGroupNodeButtonModelParameter: ILabelModelParameter;
  private urlEdgeButtonModelParameter: ILabelModelParameter;
  private addToScenarioNodeButtonModelParameter: ILabelModelParameter;
  private addToScenarioButtonStyle: ArdoqAddToScenario;
  graph: IGraph;

  rowHeight: number;
  columnWidth: number;
  rowsSource: Node[];
  tableNode?: INode;
  tableNodeStyle: TableNodeStyle;
  table: Table;
  _rowMap: Map<string, IRow | IColumn>;
  private _isVertical: boolean;

  constructor({ graph, rowHeight = 200, columnWidth = 200 }: ConstructorArgs) {
    this.graph = graph;
    this.groupsSource = [];
    this.nodesSource = [];
    this.edgesSource = [];
    this.hasGroups = false;

    this._groupNodeMap = new Map();
    this._nodeMap = new Map();
    this._edgeMap = new Map();

    // Initialization
    graph.edgeDefaults.style = new ArdoqEdgeStyle();

    // Style and labels
    const labelModel = new ExteriorNodeLabelModel();
    labelModel.margins = new Insets(CONTEXT_HIGHLIGHT_STROKE);
    graph.nodeDefaults.labels.layoutParameter = labelModel.createParameter(
      ExteriorNodeLabelModelPosition.BOTTOM
    );

    const labelStyle = ArdoqLabelStyle.Instance;
    graph.edgeDefaults.labels.style = labelStyle;
    graph.nodeDefaults.labels.style = labelStyle;

    this.urlGroupNodeButtonModelParameter =
      new FreeNodeLabelModel().createParameter(
        new Point(0, 0), // upper left corner
        new Point(0, 0),
        new Point(0.5, 0.5)
      );
    this.urlEdgeButtonModelParameter =
      new FreeEdgeLabelModel().createParameter();

    this.addToScenarioButtonStyle = new ArdoqAddToScenario();
    this.addToScenarioNodeButtonModelParameter =
      new FreeNodeLabelModel().createParameter(
        new Point(0, -0.75),
        new Point(1, 0),
        new Point(0, 1)
      );

    const edgeLabelModel = new FreeEdgeLabelModel();
    graph.edgeDefaults.labels.layoutParameter =
      edgeLabelModel.createParameter();

    const table = new Table();
    const tableNodeStyle = new TableNodeStyle(table);

    this.rowsSource = [];
    this._rowMap = new Map();

    tableNodeStyle.backgroundStyle = new ArdoqTableBackgroundStyle();

    this.rowHeight = rowHeight;
    this.columnWidth = columnWidth;
    this.table = table;
    this.tableNodeStyle = tableNodeStyle;

    this._isVertical = true; // initializing this field to true so setVertical(false) registers the change and initializes defaults.
    this.setVertical(false);
  }

  createGroupNode = (graphNode: Node | GraphGroup) => {
    const group = this.graph.createGroupNode({
      tag: graphNode,
    });
    // isGroup() doesn't seem to work for the old group nodes.
    if (graphNode instanceof GraphGroup && graphNode.hasURLFields()) {
      this.addURLNodeLabel(group, graphNode);
    }
    return group;
  };

  removeGroupNode = (groupNode: INode) => {
    this.removeItem(groupNode, this._groupNodeMap);
  };

  updateGroupNode = (graphNode: Node<GraphItemModel> | GraphGroup) =>
    this._groupNodeMap.get(graphNode.id)!;

  createNode = (graphNodeArg: Node | GraphNode) => {
    const graphNode = graphNodeArg as Node; // type assertion: we are always getting a Node here, never a GraphNode. this is not in agreement with the base class.
    const belongsToATableRow = this.findTargetRow(graphNode);

    let node;
    const truncateNodeName = true;
    if (belongsToATableRow) {
      node = this.graph.createNode({
        parent: this.tableNode,
        tag: graphNode,
        labels: [encodingUtils.unescapeHTML(graphNode.name(truncateNodeName))],
      });
    } else {
      node = this.graph.createNode({
        tag: graphNode,
        labels: [encodingUtils.unescapeHTML(graphNode.name(truncateNodeName))],
      });
    }

    if (graphNode.hasURLFields()) {
      this.graph.addLabel({
        owner: node,
        text: '',
        layoutParameter: urlNodeButtonModelParameter,
        style: ArdoqURLLabelStyle.Instance,
        tag: graphNode,
      });
    }
    this.updateNodeLayout(node);
    return node;
  };

  updateNode = (graphNode: Node | GraphNode) => {
    const node = this._nodeMap.get(graphNode.id)!;
    const style = getStyle(graphNode);

    if (style !== node.style) {
      this.graph.setStyle(node, style);
    }
    this.updateNodeLayout(node, style);
    if (
      graphNode.hasURLFields() &&
      !node.labels.find(label => isURLLabel(label))
    ) {
      this.addURLNodeLabel(node, graphNode);
    }

    if (
      graphNode instanceof GraphItem &&
      !(
        isInScopeDiffMode() &&
        graphNode.getVisualDiffType() === DiffType.PLACEHOLDER
      ) &&
      this.scenarioRelatedComponentIds.has(graphNode.modelId)
    ) {
      this.addToScenarioButton(node, graphNode);
    }

    const labelsToRemove: ILabel[] = [];
    node.labels.forEach(label => {
      if (label.tag === null) {
        this.graph.setLabelText(
          label,
          encodingUtils.unescapeHTML(graphNode.getLabel())
        );
      } else if (isURLLabel(label) && !label.tag.hasURLFields()) {
        labelsToRemove.push(label);
      }
    });
    labelsToRemove.forEach(label => this.graph.remove(label));

    this.moveNodeToRow(node);
    return node;
  };

  removeNode = (node: INode) => {
    this.removeItem(node, this._nodeMap);
  };

  createEdge = (graphEdge: Edge | GraphEdge) => {
    const edge = this.graph.createEdge({
      tag: graphEdge,
      labels: [encodingUtils.unescapeHTML(graphEdge.getLabel())],
      source:
        this._nodeMap.get(graphEdge.source) ||
        this._groupNodeMap.get(graphEdge.source)!,
      target:
        this._nodeMap.get(graphEdge.target) ||
        this._groupNodeMap.get(graphEdge.target)!,
    });

    if (graphEdge.hasURLFields()) {
      this.addURLEdgeLabel(edge, graphEdge);
    }
    return edge;
  };

  updateEdge = (graphEdge: Edge | GraphEdge) => {
    const edge = this._edgeMap.get(graphEdge.id)!;

    if (
      graphEdge.hasURLFields() &&
      !edge.labels.find(label => isURLLabel(label))
    ) {
      this.addURLEdgeLabel(edge, graphEdge);
    }

    // Create new edge if sourcePort/targetPort is gone (group Node turned into a normal node or vice-versa)
    if (!this.graph.contains(edge) || !edge.sourcePort || !edge.targetPort) {
      return this.createEdge(graphEdge);
    }

    const labelsToRemove: ILabel[] = [];
    edge.labels.forEach(label => {
      if (label.tag === null) {
        this.graph.setLabelText(
          label,
          encodingUtils.unescapeHTML(graphEdge.getLabel())
        );
      } else if (isURLLabel(label) && !label.tag.hasURLFields()) {
        labelsToRemove.push(label);
      }
    });
    labelsToRemove.forEach(label => this.graph.remove(label));

    return edge;
  };

  removeEdge = (edge: IEdge) => {
    // Make sure the edge is attached to the graph
    if (this.graph.contains(edge) && (edge.sourcePort || edge.targetPort)) {
      this.removeItem(edge, this._edgeMap);
    }
  };

  removeItem = (item: IModelItem, map: Map<string, IModelItem>) => {
    const businessObject = item.tag;
    map.delete(businessObject.id);
    if (this.graph.contains(item)) {
      this.graph.remove(item);
    }
  };

  addURLNodeLabel(owner: INode, graphNode: Node | GraphNode | GraphGroup) {
    this.graph.addLabel({
      owner,
      text: '',
      layoutParameter:
        graphNode instanceof GraphGroup
          ? this.urlGroupNodeButtonModelParameter
          : urlNodeButtonModelParameter,
      style: ArdoqURLLabelStyle.Instance,
      tag: graphNode,
    });
  }

  addURLEdgeLabel(owner: IEdge, graphEdge: Edge | GraphEdge) {
    this.graph.addLabel({
      owner,
      text: '',
      layoutParameter: this.urlEdgeButtonModelParameter,
      style: ArdoqURLLabelStyle.Instance,
      tag: graphEdge,
    });
  }

  addToScenarioButton(owner: INode, graphNode: GraphItem) {
    this.graph.addLabel({
      owner,
      text: '',
      layoutParameter: this.addToScenarioNodeButtonModelParameter,
      style: this.addToScenarioButtonStyle,
      tag: graphNode,
    });
  }

  getNode(businessObject: ComponentBackboneModel) {
    let node;
    try {
      node = this.graph.nodes.find(
        candidate => candidate.tag?.dataModel === businessObject
      );
    } catch (e) {
      logWarn(new Error(`Couldn't get node in graphBuilder.`));
    }
    return node;
  }

  _setGroupNodeParents() {
    // Set parents of group nodes
    Array.from(this._groupNodeMap.values()).forEach(groupNode => {
      const graphNode = groupNode.tag as Node | GraphGroup;
      const parent =
        graphNode.parent && this._groupNodeMap.get(graphNode.parent.id);
      if (parent) {
        if (parent === groupNode) {
          // this shouldn't happen (but it does)

          const parentGraphNode = parent.tag as Node | GraphGroup;
          logError(Error('Self-parented node in graph builder.'), null, {
            groupNodeId: graphNode.id,
            parentId: parentGraphNode.id,
            groupNodeMapKeys: Array.from(this._groupNodeMap.keys()),
            groupNodeMapNodeIds: Array.from(this._groupNodeMap.values())
              .map<Node | GraphGroup>(node => node.tag)
              .map(node => node.id),
          });
        } else {
          this.graph.setParent(groupNode, parent);
        }
      }
    });
  }

  _diffUpdateGraph<TItem extends IModelItem, TObj extends { id: string }>({
    oldMap,
    businessObjects,
    createFunc,
    updateFunc,
    removeFunc = this.removeItem,
  }: DiffUpdateGraphArgs<TItem, TObj>) {
    const newMap = new Map(
      businessObjects.map(businessObject => [
        businessObject.id,
        // Add new items, update existing items
        (oldMap.has(businessObject.id) ? updateFunc : createFunc)(
          businessObject
        ),
      ])
    );
    businessObjects.forEach(businessObject => oldMap.delete(businessObject.id));

    // Delete old items
    Array.from(oldMap.values()).forEach(graphItem =>
      removeFunc(graphItem, oldMap)
    );
    return newMap;
  }

  clearEdges() {
    Array.from(this._edgeMap.values()).forEach(this.removeEdge);
  }

  createRow = (graphNode: Node) => {
    let row;
    if (graphNode.isField()) {
      const field = (graphNode.dataModel as FieldValue).field;
      const tag = field.label;
      const findTag = (currentRow: IRow | IColumn) => currentRow.tag === tag;
      let parentRow = this.isVertical
        ? this.table.columns.find(findTag)
        : this.table.rows.find(findTag);

      if (!parentRow) {
        parentRow = this.isVertical
          ? this.table.createColumn({
              tag,
              padding: PARENT_COLUMN_INSETS,
              minWidth: 500,
            })
          : this.table.createRow({
              tag,
              padding: PARENT_ROW_INSETS,
              minHeight: 500,
            });
        this.table.addLabel({
          owner: parentRow,
          text: encodingUtils.unescapeHTML(field.label),
        });
      }
      row = this.isVertical
        ? this.table.createChildColumn({
            parent: parentRow as IColumn,
            tag: graphNode,
            padding: CHILD_COLUMN_INSETS,
          })
        : this.table.createChildRow({
            parent: parentRow as IRow,
            tag: graphNode,
            padding: CHILD_ROW_INSETS,
          });
      this.table.setStripePadding(
        parentRow,
        this.isVertical ? new Insets(175, 0, 0, 0) : new Insets(0, 0, 0, 175)
      );

      this.table.addLabel({
        owner: row,
        text: encodingUtils.unescapeHTML(graphNode.getValue()),
        tag: graphNode,
      });
    } else {
      row = this.isVertical
        ? this.table.createColumn({
            tag: graphNode,
            padding: PARENT_COLUMN_INSETS,
          })
        : this.table.createRow({ tag: graphNode, padding: PARENT_ROW_INSETS });

      this.table.addLabel({
        owner: row,
        text: encodingUtils.unescapeHTML(graphNode.getLabel()),
        tag: graphNode,
      });
    }

    return row;
  };

  updateRow = (graphNode: Node) => this._rowMap.get(graphNode.id)!;

  removeRow = (row: IRow | IColumn) => {
    this._rowMap.delete(row.tag.id);
    this.table.remove(row);
  };

  get isVertical() {
    return this._isVertical;
  }

  setVertical(vertical: boolean) {
    if (vertical === this._isVertical) {
      return;
    }
    this._isVertical = vertical;

    this.clear();
    // @ts-expect-error todo: address access modifier issues
    this.table.columnDefaults = this.table.createColumnDefaults();
    // @ts-expect-error todo: address access modifier issues
    this.table.rowDefaults = this.table.createRowDefaults();
    if (vertical) {
      this.table.columnDefaults.size = this.rowHeight;
      this.table.columnDefaults.padding = new Insets(100, 0, 0, 0);
      this.table.columnDefaults.style = new ArdoqSwimlaneStripeStyle();
      this.table.columnDefaults.labels.style = new ArdoqSwimlaneLabelStyle();
      this.table.rowDefaults.size = this.columnWidth;
      this.table.rowDefaults.style = new NodeStyleStripeStyleAdapter(
        INodeStyle.VOID_NODE_STYLE
      );
    } else {
      this.table.rowDefaults.size = this.rowHeight;
      this.table.rowDefaults.padding = new Insets(0, 0, 0, 100);
      this.table.rowDefaults.style = new ArdoqSwimlaneStripeStyle();
      this.table.rowDefaults.labels.style = new ArdoqSwimlaneLabelStyle();
      this.table.columnDefaults.size = this.columnWidth;
      this.table.columnDefaults.style = new NodeStyleStripeStyleAdapter(
        INodeStyle.VOID_NODE_STYLE
      );
    }
  }

  /** @param _unused a caller is sending a second, unused argument. ignoring this for now. */
  updateNodeLayout(node: INode, _unused?: unknown) {
    const style = getStyle(node.tag);
    const styleWidth =
      (style.size && style.size.width) || getDefaultSize().width;
    const styleHeight =
      (style.size && style.size.height) || getDefaultSize().height;
    const yAspectRatio = NODE_HEIGHT / styleHeight;
    const xAspectRatio = NODE_WIDTH / styleWidth;

    let width;
    let height;

    if (styleWidth * yAspectRatio > NODE_WIDTH) {
      width = NODE_WIDTH;
      height = styleHeight * xAspectRatio;
    } else {
      width = styleWidth * yAspectRatio;
      height = NODE_HEIGHT;
    }

    this.graph.setStyle(node, style);
    this.graph.setNodeLayout(
      node,
      new Rect(
        node.layout.topLeft,
        getNodeLayoutSize({
          styleWidth: width,
          styleHeight: height,
          hasContextHighlight: isContextNode(node),
          contextHighlightPadding: CONTEXT_HIGHLIGHT_PADDING,
          visualDiffType:
            node.tag instanceof GraphItem
              ? node.tag.getVisualDiffType()
              : DiffType.NONE,
        })
      )
    );

    this.moveNodeToRow(node);
  }

  findTargetRow(graphNodeArg: Node | GraphNode) {
    const graphNode = graphNodeArg as Node; // type assertion: we are always getting a Node here, never a GraphNode. this is not in agreement with the base class.
    const parentId = graphNode.parent && graphNode.parent.id;
    const parentRow = parentId ? this._rowMap.get(parentId) : undefined;
    const selfRow = this._rowMap.get(graphNode.id);
    return parentRow || selfRow;
  }

  moveNodeToRow(node: INode) {
    const graphNode = node.tag;
    const targetRow = this.findTargetRow(graphNode);

    if (targetRow) {
      const placementRectangle = new Rect(
        targetRow.layout.center.x - node.layout.width / 2,
        targetRow.layout.center.y - node.layout.height / 2,
        node.layout.width,
        node.layout.height
      );
      this.graph.setNodeLayout(node, placementRectangle);
    }
  }

  clear() {
    this.graph.clear();

    this._groupNodeMap.clear();
    this._nodeMap.clear();
    this._edgeMap.clear();

    this.table.clear();
    this._rowMap.clear();
  }

  hasLayoutUpdate(args: TableGraphBuilderHasLayoutArgs) {
    const { nodes, groups, edges, rows } = args;
    const nodesUnchanged = !nodes || isEqual(nodes, this.nodesSource);
    const groupsUnchanged = !groups || isEqual(groups, this.groupsSource);
    const edgesUnchanged = !edges || isEqual(edges, this.edgesSource);
    const rowsUnchanged = !rows || isEqual(rows, this.rowsSource);

    return !(
      nodesUnchanged &&
      groupsUnchanged &&
      edgesUnchanged &&
      rowsUnchanged
    );
  }

  buildGraph() {
    this.clear();
    if (this.isVertical) {
      this.table.createRow(this.columnWidth);
    } else {
      this.table.createColumn(this.columnWidth);
    }
    this.tableNode = this.graph.createGroupNode({
      layout: this.table.layout.toRect(),
      style: this.tableNodeStyle,
    });
    this.graph.setNodeCenter(this.tableNode, Point.ORIGIN);

    this._rowMap = new Map(
      this.rowsSource.map(graphNode => [
        graphNode.id,
        this.createRow(graphNode),
      ])
    );
    this._nodeMap = new Map(
      this.nodesSource.map(graphNode => [
        graphNode.id,
        this.createNode(graphNode as Node),
      ])
    );
    this._edgeMap = new Map(
      this.edgesSource.map(graphEdge => [
        graphEdge.id,
        this.createEdge(graphEdge as Edge),
      ])
    );
  }

  updateGraph({ isReordered = false } = {}) {
    if (
      isReordered ||
      !this.tableNode ||
      !this.graph.nodes.includes(this.tableNode)
    ) {
      this.buildGraph();
      return;
    }
    this._rowMap = this._diffUpdateGraph({
      oldMap: this._rowMap,
      businessObjects: this.rowsSource,
      createFunc: this.createRow,
      updateFunc: this.updateRow,
      removeFunc: this.removeRow,
    });
    this.graph.setNodeLayout(this.tableNode, this.table.layout.toRect());

    if (
      this._nodeMap.size + this._edgeMap.size + this._groupNodeMap.size ===
      0
    ) {
      this.buildGraph();
      return;
    }

    this._groupNodeMap = this._diffUpdateGraph({
      oldMap: this._groupNodeMap,
      businessObjects: this.groupsSource,
      createFunc: this.createGroupNode,
      updateFunc: this.updateGroupNode,
    });

    this.hasGroups = this.groupsSource.length > 0;

    this._setGroupNodeParents();

    this._nodeMap = this._diffUpdateGraph({
      oldMap: this._nodeMap,
      businessObjects: this.nodesSource,
      createFunc: this.createNode,
      updateFunc: this.updateNode,
    });

    this._edgeMap = this._diffUpdateGraph({
      oldMap: this._edgeMap,
      businessObjects: this.edgesSource,
      createFunc: this.createEdge,
      updateFunc: this.updateEdge,
    });
  }
}
